import React from 'react';
import { Views, View, NavigateAction } from 'react-big-calendar';
import TimeGrid from 'react-big-calendar/lib/TimeGrid';
import { Reservation } from '../../types/reservation';
import {
  calendarLocalizer,
  calendarMessages,
  getReservationCalendarDays,
  getReservationCalendarRange,
  navigateReservationCalendar,
} from '../../utils/calendarUtils';

// The 3-day view lives in the unused work_week slot so the View union type
// keeps working across the page; only its label changes here.
export const reservationsCalendarMessages = { ...calendarMessages, work_week: '3 días' };

export const getInitialDateRange = (isMobile: boolean, hiddenWeekdays: readonly number[]): { start: Date; end: Date } =>
  getReservationCalendarRange(isMobile ? Views.DAY : Views.WEEK, new Date(), hiddenWeekdays);

export type CalendarEvent = Reservation;

function getDateRangeLabel(start: Date, end: Date): string {
  const startMonth = calendarLocalizer.format(start, 'MM', 'es');
  const endMonth = calendarLocalizer.format(end, 'MM', 'es');
  if (startMonth !== endMonth) {
    return `${calendarLocalizer.format(start, "dd 'de' MMMM", 'es')} a ${calendarLocalizer.format(end, "dd 'de' MMMM", 'es')}`;
  }
  return `${calendarLocalizer.format(start, 'dd', 'es')} a ${calendarLocalizer.format(end, "dd 'de' MMMM", 'es')}`;
}

export function getToolbarLabel(view: View, date: Date, hiddenWeekdays: readonly number[] = []): string {
  if (view === Views.DAY) return calendarLocalizer.format(date, "dd 'de' MMMM", 'es');
  const range = getReservationCalendarRange(view, date, hiddenWeekdays);
  return getDateRangeLabel(range.start, range.end);
}

type ReservationTimeGridViewProps = React.ComponentProps<typeof TimeGrid> & { hiddenWeekdays?: readonly number[] };

// `hiddenWeekdays` is a Calendar prop forwarded to the view and its navigate() (like Agenda's `length`);
// the static range() only receives the localizer, so handleRangeChange applies it to the fetch window.
function HiddenWeekdaysWeekView({ date, hiddenWeekdays, ...timeGridProps }: ReservationTimeGridViewProps): JSX.Element {
  return (
    <TimeGrid
      {...timeGridProps}
      date={date}
      range={getReservationCalendarDays(Views.WEEK, date, hiddenWeekdays)}
      eventOffset={15}
    />
  );
}

HiddenWeekdaysWeekView.range = (date: Date): Date[] => getReservationCalendarDays(Views.WEEK, date);
HiddenWeekdaysWeekView.navigate = (date: Date, action: NavigateAction): Date =>
  navigateReservationCalendar(Views.WEEK, date, action);
HiddenWeekdaysWeekView.title = (date: Date): string => getToolbarLabel(Views.WEEK, date);

function ThreeDayView({ date, hiddenWeekdays, ...timeGridProps }: ReservationTimeGridViewProps): JSX.Element {
  return (
    <TimeGrid
      {...timeGridProps}
      date={date}
      range={getReservationCalendarDays(Views.WORK_WEEK, date, hiddenWeekdays)}
      eventOffset={15}
    />
  );
}

ThreeDayView.range = (date: Date): Date[] => getReservationCalendarDays(Views.WORK_WEEK, date);
ThreeDayView.navigate = (date: Date, action: NavigateAction, { hiddenWeekdays }: { hiddenWeekdays?: readonly number[] }): Date =>
  navigateReservationCalendar(Views.WORK_WEEK, date, action, hiddenWeekdays);
ThreeDayView.title = (date: Date): string => getToolbarLabel(Views.WORK_WEEK, date);

export const CALENDAR_VIEWS = { week: true, work_week: ThreeDayView, day: true };
export const HIDDEN_WEEKDAYS_CALENDAR_VIEWS = { ...CALENDAR_VIEWS, week: HiddenWeekdaysWeekView };
